import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { startOfDay, subDays, format, startOfMonth, endOfMonth } from "date-fns";
import { getPeopleCount } from "@/lib/ticket-utils";

interface DailySales {
  date: string;
  total: number;
  count: number;
}

interface PaymentMethodSales {
  name: string;
  total: number;
  count: number;
}

interface TopProduct {
  name: string;
  quantity: number;
  total: number;
}

interface CategorySales {
  name: string;
  total: number;
  count: number;
}

interface ReportsData {
  dailySales: DailySales[];
  paymentMethodSales: PaymentMethodSales[];
  topProducts: TopProduct[];
  categorySales: CategorySales[];
  totalSales: number;
  totalOrders: number;
  averageTicket: number;
}

interface UseReportsParams {
  dateFrom: Date;
  dateTo: Date;
  periodId?: string;
}

export function useReports(params: UseReportsParams) {
  const { activeRestaurant } = useRestaurantContext();
  const { dateFrom, dateTo, periodId } = params;

  return useQuery({
    queryKey: ["reports", activeRestaurant?.id, dateFrom.toISOString(), dateTo.toISOString(), periodId],
    queryFn: async (): Promise<ReportsData> => {
      if (!activeRestaurant) {
        return {
          dailySales: [],
          paymentMethodSales: [],
          topProducts: [],
          categorySales: [],
          totalSales: 0,
          totalOrders: 0,
          averageTicket: 0,
        };
      }

      const dateFromStr = startOfDay(dateFrom).toISOString();
      const dateToStr = new Date(dateTo.setHours(23, 59, 59, 999)).toISOString();

      // Build the query
      let query = supabase
        .from("orders")
        .select(`
          id,
          total,
          created_at,
          people_count,
          order_type,
          order_items(product_name, quantity, total_price, product_id),
          order_payments(amount, payment_method:payment_methods(name))
        `)
        .eq("restaurant_id", activeRestaurant.id)
        .neq("status", "cancelled");

      // Filter by period_id if provided, otherwise use date range
      if (periodId) {
        query = query.eq("period_id", periodId);
      } else {
        query = query.gte("created_at", dateFromStr).lte("created_at", dateToStr);
      }

      const { data: orders, error: ordersError } = await query;

      if (ordersError) throw ordersError;

      // Fetch products with categories for category sales
      const { data: products } = await supabase
        .from("products")
        .select("id, name, category:categories(name)")
        .eq("restaurant_id", activeRestaurant.id);

      const productCategoryMap = new Map(
        products?.map((p) => [p.id, p.category?.name || "Sem Categoria"]) || []
      );

      // Calculate daily sales
      const dailySalesMap = new Map<string, { total: number; count: number }>();
      orders?.forEach((order) => {
        const date = format(new Date(order.created_at), "yyyy-MM-dd");
        const existing = dailySalesMap.get(date) || { total: 0, count: 0 };
        dailySalesMap.set(date, {
          total: existing.total + Number(order.total),
          count: existing.count + 1,
        });
      });

      const dailySales: DailySales[] = Array.from(dailySalesMap.entries())
        .map(([date, data]) => ({
          date: format(new Date(date), "dd/MM"),
          total: data.total,
          count: data.count,
        }))
        .sort((a, b) => a.date.localeCompare(b.date));

      // Calculate payment method sales
      const paymentMap = new Map<string, { total: number; count: number }>();
      orders?.forEach((order) => {
        order.order_payments?.forEach((payment: any) => {
          const name = payment.payment_method?.name || "Outro";
          const existing = paymentMap.get(name) || { total: 0, count: 0 };
          paymentMap.set(name, {
            total: existing.total + Number(payment.amount),
            count: existing.count + 1,
          });
        });
      });

      const paymentMethodSales: PaymentMethodSales[] = Array.from(paymentMap.entries())
        .map(([name, data]) => ({
          name,
          total: data.total,
          count: data.count,
        }))
        .sort((a, b) => b.total - a.total);

      // Calculate top products
      const productMap = new Map<string, { quantity: number; total: number }>();
      orders?.forEach((order) => {
        order.order_items?.forEach((item: any) => {
          const existing = productMap.get(item.product_name) || { quantity: 0, total: 0 };
          productMap.set(item.product_name, {
            quantity: existing.quantity + item.quantity,
            total: existing.total + Number(item.total_price),
          });
        });
      });

      const topProducts: TopProduct[] = Array.from(productMap.entries())
        .map(([name, data]) => ({
          name,
          quantity: data.quantity,
          total: data.total,
        }))
        .sort((a, b) => b.quantity - a.quantity)
        .slice(0, 10);

      // Calculate category sales
      const categoryMap = new Map<string, { total: number; count: number }>();
      orders?.forEach((order) => {
        order.order_items?.forEach((item: any) => {
          const category = productCategoryMap.get(item.product_id) || "Sem Categoria";
          const existing = categoryMap.get(category) || { total: 0, count: 0 };
          categoryMap.set(category, {
            total: existing.total + Number(item.total_price),
            count: existing.count + item.quantity,
          });
        });
      });

      const categorySales: CategorySales[] = Array.from(categoryMap.entries())
        .map(([name, data]) => ({
          name,
          total: data.total,
          count: data.count,
        }))
        .sort((a, b) => b.total - a.total);

      // Calculate totals
      const totalSales = orders?.reduce((sum, o) => sum + Number(o.total), 0) || 0;
      const totalOrders = orders?.length || 0;
      const totalPeople = orders?.reduce((sum, o) => sum + getPeopleCount(o), 0) || 0;
      const averageTicket = totalPeople > 0 ? totalSales / totalPeople : 0;

      return {
        dailySales,
        paymentMethodSales,
        topProducts,
        categorySales,
        totalSales,
        totalOrders,
        averageTicket,
      };
    },
    enabled: !!activeRestaurant,
  });
}
